import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { IconArrowUpRight as ArrowUpRight } from '@tabler/icons-react';
import LikeButton from './LikeButton';
import Avatar from './Avatar';
import { currencySymbol } from '@/utils/currency';

export type EventCardProps = {
    event: {
        id?: string;
        title: string;
        date: string;
        location: string;
        image: string;
        price: string;
        priceType?: string;
        currency?: string; // 'Paid' | 'Free' | 'Donation' or similar
        likes: number | string;
        isNew?: boolean;
        organizer?: {
            name?: string;
            avatar?: string;
        };
        times?: Array<{
            startDate: string;
            endDate: string;
        }>;
        attachments?: {
            banner?: {
                link: string;
            };
        };
        _id?: string;
        address?: {
            fullAddress: string;
            city?: string;
        };
        venue?: string;
        likeStats?: {
            totalLikes: number;
            isLikedByCurrentUser: boolean;
            eventId: string;
        };
        showOnlyCity?: number;
        handle?: string; // Custom event handle for URL
    };
    userInfo: any,
    organizerInfo: any,
    onLikeChange?: (eventId: string, likes: number, isLiked: boolean) => void;
    iframe?: boolean;
};

export default function EventCard({ event, userInfo, organizerInfo, onLikeChange, iframe = false }: EventCardProps) {
    if (!event) return null;
    const navigate = useNavigate();
    // Use custom handle if available, otherwise fall back to ID or title slug
    //const eventUrl = event.handle ? `/e/${event.handle}` : `/event/${event._id || event.title?.replace(/\W+/g, '-').toLowerCase()}`;
    const eventUrl = `/event/${event._id || event.title?.replace(/\W+/g, '-').toLowerCase()}`;
    const image = event?.attachments?.banner?.link

    // Check event status based on start and end dates
    const eventStartDate = event.times?.[0]?.startDate;
    const eventEndDate = event.times?.[0]?.endDate;

    const isEventNew = eventStartDate ? moment(eventStartDate).isAfter(moment(), 'day') : false;
    const isEventPassed = eventEndDate ? moment(eventEndDate).isBefore(moment(), 'day') : false;

    const place = event.showOnlyCity === 1
        ? (event.address?.city || event.venue || '')
        : (event.venue || '');
    const priceLabel = event.priceType === 'Free'
        ? 'Free'
        : (event.price ? `${currencySymbol(event.currency)}${event.price}` : '');
    const start = eventStartDate ? moment(eventStartDate) : null;

    const host = organizerInfo
        ? {
            name: organizerInfo.orgName,
            avatar: <img src={organizerInfo.attachments?.avatar?.link || '/host-avatar.png'} alt={organizerInfo.orgName} className="h-6 w-6 rounded-full object-cover ring-1 ring-white/30" />,
            href: `/organization/${organizerInfo._id}`,
        }
        : userInfo
            ? {
                name: userInfo.firstName,
                avatar: <Avatar src={userInfo.profileImage} name={userInfo.firstName} seed={userInfo.id} size={24} className="ring-1 ring-white/30" />,
                href: `/user/${userInfo.id}`,
            }
            : null;

    const goToHost = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (host) navigate(host.href);
    };

    return (
        <div className="px-0 py-4 sm:p-4">
            <Link
                to={eventUrl}
                target={iframe ? '_top' : '_self'}
                className="group relative block aspect-[4/5] overflow-hidden rounded-[28px] bg-grey10 ring-1 ring-black/[0.06] transition-transform duration-300 hover:-translate-y-1 dark:ring-white/10"
            >
                {image ? (
                    <img
                        src={image}
                        alt={event.title || ''}
                        loading="lazy"
                        decoding="async"
                        className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] ${isEventPassed ? 'grayscale' : ''}`}
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary10 via-[#0b2324] to-[#0a0a0a]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-black/10" />

                <div className="absolute inset-x-4 top-4 flex items-start justify-between gap-2">
                    {start ? (
                        <div className="flex flex-col items-center rounded-2xl bg-white px-3 py-2 text-center text-[#0a0a0a] shadow-lg">
                            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">{start.format('ddd')}</span>
                            <span className="text-2xl font-bold leading-none tracking-[-0.04em]">{start.format('D')}</span>
                            <span className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-grey40">{start.format('MMM')}</span>
                        </div>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                        {isEventPassed ? (
                            <span className="flex h-8 items-center rounded-full bg-white/15 px-3 text-[11px] font-bold uppercase tracking-[0.1em] text-white">Ended</span>
                        ) : isEventNew ? (
                            <span className="flex h-8 items-center rounded-full bg-nightAccent px-3 text-[11px] font-bold uppercase tracking-[0.1em] text-[#0a0a0a]">New</span>
                        ) : null}
                        <LikeButton
                            eventId={event._id || event.id || ''}
                            initialLikes={event.likes as number || 0}
                            likeStats={event.likeStats}
                            className="!size-9 !rounded-full bg-white/15 hover:bg-white/25"
                            refetchQueries={['events', 'relatedEvents']}
                            iconOnly
                            onLikeChange={(likes, isLiked) => {
                                if (onLikeChange) {
                                    onLikeChange(event._id || event.id || '', likes, isLiked);
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-5">
                    {start && (
                        <div className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-nightAccent">
                            {start.format('h:mm A')}{place ? ` · ${place}` : ''}
                        </div>
                    )}
                    <h3 className="line-clamp-2 text-[22px] font-bold leading-[1.08] tracking-[-0.03em] text-white">
                        {event.title || ''}
                    </h3>
                    {!start && place && <div className="mt-2 truncate text-sm capitalize text-white/70">{place}</div>}

                    <div className="mt-4 flex items-center justify-between gap-3">
                        {host ? (
                            <span
                                role="link"
                                tabIndex={0}
                                onClick={goToHost}
                                onKeyDown={(e) => { if (e.key === 'Enter') goToHost(e); }}
                                className="flex min-w-0 cursor-pointer items-center gap-2 text-[13px] font-medium text-white/75 hover:text-white"
                            >
                                {host.avatar}
                                <span className="truncate capitalize">{host.name}</span>
                            </span>
                        ) : <span />}
                        <span className="flex shrink-0 items-center gap-2">
                            {priceLabel && (
                                <span className={`rounded-full px-3 py-1.5 text-[13px] font-bold ${event.priceType === 'Free' ? 'bg-nightAccent text-[#0a0a0a]' : 'bg-white text-[#0a0a0a]'}`}>
                                    {priceLabel}
                                </span>
                            )}
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition-transform duration-300 group-hover:rotate-45">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </span>
                    </div>
                </div>
            </Link>
        </div>
    );
}
